import {
    createSlice,
    PayloadAction,
} from "scholastic-client-components/src/SharedPackages/reduxToolkit"
import {NameView} from "scholastic-client-components/src/types/view/common/NameView"
import {APIStatus} from "scholastic-client-components/src/types/APITypes"
import {RegisterNewlineResponse} from "../api/deviceAPI"
import {
    Country,
    NetworksResponse,
    ScreensResponse,
    TrialEditNetwork,
    TrialEditProduct,
    TrialEditResponse,
} from "../api/trialAPI"
import {NewlineDeviceFormValues} from "../Pages/DevicePage/DeviceForm"
import {scholasticProductSortIndex} from "scholastic-client-components/src/types/scholasticProductTypes"
import {i18n} from "scholastic-client-components/src/i18n/i18n"
import {CountrySelector} from "../Pages/DevicePage/CountrySelector"

interface Location {
    id: number
    name: string
    buildings: NameView[]
    country?: Country
}

interface SliceState {
    locations: Location[]
    locationsWCreatable: Location[]
    buildingTypes: Array<{
        id: number
        name: string
        isDefault: boolean
    }>
    states: NameView[]
    countries: Array<Country>
    screens: NameView[]
    products: TrialEditProduct[]
    networks: TrialEditNetwork[]
    status: APIStatus
    initialValues?: Partial<NewlineDeviceFormValues>
    error?: string
    registerNewlineStatus: APIStatus
    networksStatus: APIStatus
    screensStatus: APIStatus
    registerNewlineResponse?: RegisterNewlineResponse & {onlyDevice: boolean}
    preassignedFields?: {
        location: boolean
        building: boolean
    }
}

const initialState = {
    locations: [],
    locationsWCreatable: [],
    buildingTypes: [],
    states: [],
    countries: [],
    screens: [],
    products: [],
    networks: [],
    status: APIStatus.Initial,
    networksStatus: APIStatus.Initial,
    screensStatus: APIStatus.Initial,
    registerNewlineStatus: APIStatus.Initial,
} as SliceState

export const NEW_ITEM_ID = -1
const getNewItem = () => ({
    id: NEW_ITEM_ID,
    name: i18n.t("auth_portal:device_page.create_new_option"),
})

export const trialEditSlice = createSlice({
    name: "trialEdit",
    initialState,
    reducers: {
        startFetch(state: SliceState) {
            state.status = APIStatus.Loading
        },
        failFetch(state: SliceState) {
            state.status = APIStatus.Failure
        },
        startFetchNetworks(state: SliceState) {
            state.networksStatus = APIStatus.Loading
        },
        failFetchNetworks(state: SliceState) {
            state.networksStatus = APIStatus.Failure
        },
        successFetchNetworks(
            state: SliceState,
            action: PayloadAction<{
                networks: NetworksResponse["networks"]
            }>
        ) {
            state.networks = action.payload.networks
            state.networksStatus = APIStatus.Success
        },
        resetNetworks(state: SliceState) {
            if (!state.initialValues) {
                state.initialValues = {}
            }
            state.initialValues.networks = []
            state.networks = []
            state.networksStatus = APIStatus.Initial
        },
        startFetchScreens(state: SliceState) {
            state.screensStatus = APIStatus.Loading
        },
        failFetchScreens(state: SliceState) {
            state.screensStatus = APIStatus.Failure
        },
        successFetchScreens(state: SliceState, action: PayloadAction<ScreensResponse>) {
            state.screens = action.payload.screens
            state.screensStatus = APIStatus.Success
        },
        resetScreens(state: SliceState) {
            state.screens = []
            state.screensStatus = APIStatus.Initial
        },
        successTrialEdit(state: SliceState, action: PayloadAction<TrialEditResponse>) {
            const {
                locations = [],
                buildingTypes = [],
                states = [],
                products = [],
                countries = [],
                location,
                building,
                allowCreateLocations,
            } = action.payload

            state.locations = locations
            state.locationsWCreatable = allowCreateLocations
                ? [{...getNewItem(), buildings: []}, ...locations].map((d) => ({
                      ...d,
                      buildings: [getNewItem(), ...d.buildings],
                  }))
                : locations
            const buildLocation = location && state.locations.find((d) => d.id === location.id)
            const initialLocation =
                buildLocation ||
                (allowCreateLocations && state.locationsWCreatable[1]) ||
                state.locationsWCreatable[0]
            const buildBuilding =
                building && initialLocation?.buildings.find((s) => s.id === building.id)
            const initialBuilding =
                buildBuilding ||
                (allowCreateLocations && initialLocation?.buildings?.[1]) ||
                initialLocation?.buildings?.[0]

            const initCountry = CountrySelector.getInitCountry(countries)

            state.initialValues = {
                location: initialLocation,
                building: initialBuilding,
                buildingType: buildingTypes.find((buildingType) => buildingType.isDefault),
                country: initCountry,
                state: initCountry?.withStates ? states[0] : undefined,
                networks: [],
                formType: "NEW_SCREEN",
            }

            state.preassignedFields = {
                location: !!buildLocation,
                building: !!buildBuilding,
            }

            state.buildingTypes = buildingTypes
            state.states = states
            state.countries = countries
            state.products = products.sort(
                (a, b) => scholasticProductSortIndex[a.type] - scholasticProductSortIndex[b.type]
            )
            state.status = APIStatus.Success
        },

        setError(state: SliceState, action: PayloadAction<string>) {
            state.error = action.payload
        },

        startRegisterNewline(state: SliceState) {
            state.registerNewlineStatus = APIStatus.Loading
        },
        failRegisterNewline(state: SliceState) {
            state.registerNewlineStatus = APIStatus.Failure
        },
        successRegisterNewline(
            state: SliceState,
            action: PayloadAction<SliceState["registerNewlineResponse"]>
        ) {
            state.registerNewlineResponse = action.payload
            state.registerNewlineStatus = APIStatus.Success
        },
        reset() {
            return initialState
        },
    },
})

interface Store {
    trialNetworks: SliceState
}

export const trialEditSelectors = {
    getLocations: (store: Store) => store.trialNetworks.locations,
    getLocationsWCreatable: (store: Store) => store.trialNetworks.locationsWCreatable,
    getStatus: (store: Store) => store.trialNetworks.status,
    getInitialValues: (store: Store) => store.trialNetworks.initialValues,
    getError: (store: Store) => store.trialNetworks.error,
    getBuildingTypes: (store: Store) => store.trialNetworks.buildingTypes,
    getStates: (store: Store) => store.trialNetworks.states,
    getCountries: (store: Store) => store.trialNetworks.countries,
    getScreens: (store: Store) => store.trialNetworks.screens,
    getScreensStatus: (store: Store) => store.trialNetworks.screensStatus,
    getProducts: (store: Store) => store.trialNetworks.products,
    getNetworks: (store: Store) => store.trialNetworks.networks,
    getNetworksStatus: (store: Store) => store.trialNetworks.networksStatus,
    getRegisterNewlineStatus: (store: Store) => store.trialNetworks.registerNewlineStatus,
    getRegisterNewlineResponse: (store: Store) => store.trialNetworks.registerNewlineResponse,
    getPreassignedFields: (store: Store) => store.trialNetworks.preassignedFields,
}
